// ==========================
// Imports
// ==========================
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { supabase } from "../../../lib/supa";
import { useParams, useNavigate } from "react-router-dom";

// ==========================
// Helpers
// ==========================
const renderStatus = (status) => {
  const s = String(status || "").toLowerCase();
  const map = {
    active: { label: "ACTIVE", color: "success" },
    inactive: { label: "INACTIVE", color: "secondary" },
    suspended: { label: "SUSPENDED", color: "error" },
  };
  const { label, color } = map[s] || {
    label: (status || "—").toString().toUpperCase(),
    color: "info",
  };
  return <MDBadge badgeContent={label} color={color} variant="gradient" size="sm" />;
};

// ==========================
// Component
// ==========================
export default function ClientView() {
  const { id: CLIENT_ID } = useParams();
  const navigate = useNavigate();

  // Data state
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    salutation: "",
    company: "",
    email: "",
    mobile: "",
    phone: "",
    status: "active",
  });
  const [initialForm, setInitialForm] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Load client once
  useEffect(() => {
    let on = true;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const { data, error } = await supabase
          .from("clients")
          .select("first_name,last_name,salutation,company,email,mobile,phone,status")
          .eq("id", CLIENT_ID)
          .single();
        if (error) throw error;

        const payload = {
          first_name: data?.first_name || "",
          last_name: data?.last_name || "",
          salutation: data?.salutation || "",
          company: data?.company || "",
          email: data?.email || "",
          mobile: data?.mobile || "",
          phone: data?.phone || "",
          status: data?.status || "active",
        };

        if (on) {
          setForm(payload);
          setInitialForm(payload);
        }
      } catch (e) {
        if (on) setLoadError(e?.message || "Σφάλμα φόρτωσης");
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [CLIENT_ID]);

  // Handlers
  const handleEdit = () => {
    // Μπαίνουμε σε edit mode (αν δεν είμαστε ήδη)
    if (!isEditing) {
      setFormError(null);
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    // Επιστροφή στις αρχικές τιμές
    if (initialForm) setForm(initialForm);
    setFormError(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setFormError(null);

    if (
      !form.first_name.trim() ||
      !form.last_name.trim() ||
      !form.email.trim() ||
      !form.mobile.trim()
    ) {
      setFormError("Συμπλήρωσε ΟΝΟΜΑ, ΕΠΙΘΕΤΟ, EMAIL, ΚΙΝΗΤΟ.");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("clients")
        .update({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          salutation: form.salutation || null,
          company: form.company || null,
          email: form.email.trim(),
          mobile: form.mobile.trim(),
          phone: form.phone?.trim() || null,
          status: form.status || "active",
        })

        .eq("id", CLIENT_ID)
        .select("id")
        .single();

      if (error) throw error;

      // Οριστικοποίηση αλλαγών
      setInitialForm(form);
      setIsEditing(false);
    } catch (err) {
      setFormError(err?.message || "Αποτυχία αποθήκευσης.");
    } finally {
      setSaving(false);
    }
  };

  // ==========================
  // Render
  // ==========================
  const fullName = `${form.first_name || ""} ${form.last_name || ""}`.trim() || "—";

  // Read-only styling for inputs (divider border)
  const readOnlyInputSx = isEditing
    ? {}
    : {
        "& .MuiOutlinedInput-root": {
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" }, // or 'divider'
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
          backgroundColor: "divider",
        },
      };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {/* Header */}
              <MDBox
                mx={2}
                mt={-3}
                py={1}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="h6" color="white">
                    {loading ? "ΦΟΡΤΩΣΗ…" : fullName}
                  </MDTypography>

                  {/* Actions: Services (left) + Edit (right) */}
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDAvatar
                      size="sm"
                      bgColor="info"
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/services?client=${CLIENT_ID}`)}
                      role="button"
                      tabIndex={0}
                      title="Υπηρεσίες πελάτη"
                      onKeyDown={(e) =>
                        (e.key === "Enter" || e.key === " ") &&
                        navigate(`/services?client=${CLIENT_ID}`)
                      }
                    >
                      <Icon sx={{ color: "white" }}>manage_accounts</Icon>
                    </MDAvatar>

                    <MDAvatar
                      size="sm"
                      bgColor={isEditing ? "secondary" : "warning"}
                      sx={{
                        cursor: isEditing ? "not-allowed" : "pointer",
                        opacity: isEditing ? 0.6 : 1,
                      }}
                      onClick={() => !isEditing && handleEdit()}
                      role="button"
                      tabIndex={0}
                      title={isEditing ? "Επεξεργασία σε εξέλιξη" : "Επεξεργασία"}
                      onKeyDown={(e) =>
                        !isEditing && (e.key === "Enter" || e.key === " ") && handleEdit()
                      }
                    >
                      <Icon sx={{ color: "white" }}>mode_edit</Icon>
                    </MDAvatar>
                  </MDBox>
                </MDBox>
              </MDBox>

              {/* Στοιχεία Πελάτη (φόρμα inline) */}
              <MDBox p={3}>
                {loading ? (
                  <MDTypography variant="button" color="text">
                    ΦΟΡΤΩΣΗ…
                  </MDTypography>
                ) : loadError ? (
                  <MDTypography variant="button" color="error">
                    ΣΦΑΛΜΑ: {loadError}
                  </MDTypography>
                ) : (
                  <>
                    {formError && (
                      <MDBox mb={2} color="error.main">
                        {formError}
                      </MDBox>
                    )}

                    <Grid container spacing={2}>
                      {/* ΟΝΟΜΑ */}
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="ΟΝΟΜΑ"
                          fullWidth
                          size="small"
                          value={form.first_name}
                          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                          InputProps={{ readOnly: !isEditing }}
                          sx={readOnlyInputSx}
                        />
                      </Grid>
                      {/* ΕΠΙΘΕΤΟ */}
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="ΕΠΙΘΕΤΟ"
                          fullWidth
                          size="small"
                          value={form.last_name}
                          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                          InputProps={{ readOnly: !isEditing }}
                          sx={readOnlyInputSx}
                        />
                      </Grid>
                      {/* ΠΡΟΣΦΩΝΗΣΗ */}
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="ΠΡΟΣΦΩΝΗΣΗ"
                          fullWidth
                          size="small"
                          placeholder="π.χ. ΚΥΡΙΟΣ, ΚΥΡΙΑ, Δρ., κα."
                          value={form.salutation}
                          onChange={(e) => setForm({ ...form, salutation: e.target.value })}
                          inputProps={{ maxLength: 30, autoComplete: "honorific-prefix" }}
                          InputProps={{ readOnly: !isEditing }}
                          sx={readOnlyInputSx}
                        />
                      </Grid>
                      {/* ΕΤΑΙΡΕΙΑ */}
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="ΕΤΑΙΡΕΙΑ"
                          fullWidth
                          size="small"
                          value={form.company}
                          onChange={(e) => setForm({ ...form, company: e.target.value })}
                          InputProps={{ readOnly: !isEditing }}
                          sx={readOnlyInputSx}
                        />
                      </Grid>
                      {/* EMAIL */}
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="EMAIL"
                          type="email"
                          fullWidth
                          size="small"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          InputProps={{ readOnly: !isEditing }}
                          sx={readOnlyInputSx}
                        />
                      </Grid>
                      {/* ΚΙΝΗΤΟ */}
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="ΚΙΝΗΤΟ"
                          fullWidth
                          size="small"
                          value={form.mobile}
                          onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                          InputProps={{ readOnly: !isEditing }}
                          sx={readOnlyInputSx}
                        />
                      </Grid>
                      {/* ΣΤΑΘΕΡΟ */}
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          label="ΣΤΑΘΕΡΟ"
                          fullWidth
                          size="small"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          InputProps={{ readOnly: !isEditing }}
                          sx={readOnlyInputSx}
                        />
                      </Grid>
                      {/* ΚΑΤΑΣΤΑΣΗ */}
                      <Grid item xs={12} sm={6}>
                        {/* Toggle αντί για Select */}
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.status === "active"}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  status: e.target.checked ? "active" : "inactive",
                                })
                              }
                              color={form.status === "active" ? "info" : "secondary"}
                              disabled={!isEditing}
                            />
                          }
                          label={form.status === "active" ? "ΕΝΕΡΓΟΣ" : "ΑΝΕΝΕΡΓΟΣ"}
                        />
                      </Grid>
                    </Grid>

                    {/* Actions */}
                    {isEditing && (
                      <MDBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
                        <MDButton
                          variant="text"
                          color="secondary"
                          onClick={handleCancel}
                          disabled={saving}
                        >
                          ΑΚΥΡΩΣΗ
                        </MDButton>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? "ΑΠΟΘΗΚΕΥΣΗ..." : "ΑΠΟΘΗΚΕΥΣΗ"}
                        </MDButton>
                      </MDBox>
                    )}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
